import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Gauge, RefreshCw, X, Search as SearchIcon, ShieldCheck, Activity,
  AlertTriangle, TrendingDown, CheckCircle2, Eye, PauseCircle, PlayCircle, ChevronRight,
} from "lucide-react";

// Local FR/EN dictionary (same convention as AdminRiskCenter). No hardcoded
// user-facing strings; internal identifiers (statuses/actions) are NOT translated.
const TXT = {
  fr: {
    pageTitle: "Qualité des services",
    pageSub: "Intelligence qualité — analyse et recommandations. Aucune dépublication automatique : l'administrateur décide.",
    refresh: "Actualiser",
    total: "Services analysés", degraded: "Dégradation détectée", monitoring: "En surveillance", suspended: "Suspendus",
    autoOff: "Suspension auto : DÉSACTIVÉE", autoOn: "Suspension auto : ACTIVÉE",
    filters: "Filtres", allStatuses: "Tous les statuts", allActions: "Toutes les actions", allRisks: "Tous les risques",
    onlyDegraded: "Dégradés uniquement",
    search: "Rechercher un service…",
    colService: "Service", colProvider: "Fournisseur", colPlatform: "Plateforme", colStatus: "Statut",
    colQuality: "Score qualité", colConfidence: "Confiance", colAction: "Action recommandée", colRisk: "Risque",
    colOrders: "Commandes", colComplaints: "Réclamations", colDegr: "Dégradation", colDetail: "Détail",
    view: "Voir", noItems: "Aucun service.",
    scoreLabel: "Score qualité", confidenceLabel: "Niveau de confiance", statusLabel: "Statut",
    recommendation: "Action recommandée", reasons: "Raisons de la recommandation", nextAction: "Prochaine action",
    metricsUsed: "Métriques utilisées", unavailable: "Données indisponibles",
    recentVsHist: "Évolution récente vs historique", recentQ: "Qualité récente", histQ: "Qualité historique",
    events: "Événements qualité", complaints: "Réclamations attribuées", noComplaints: "Aucune réclamation attribuée.",
    supplierClaims: "Affirmations fournisseur (non vérifiées)", detectedRisks: "Risques détectés (métadonnées)",
    contradictions: "Contradictions", warnings: "Avertissements", missing: "Informations manquantes",
    completion: "Taux de complétion", partial: "Taux partiel", refund: "Taux de remboursement", complaintRate: "Taux de réclamations",
    orderCount: "Commandes", recentOrders: "Commandes récentes",
    decisionHistory: "Historique des décisions",
    actions: "Actions administrateur", reason: "Motif (obligatoire)",
    approve: "Approuver", keep: "Garder publié", monitor: "Surveiller", suspend: "Suspendre", reactivate: "Réactiver", reanalyze: "Ré-analyser",
    reasonRequired: "Un motif est requis pour cette action", actionDone: "Action appliquée", reanalyzed: "Service ré-analysé",
    detailTitle: "Détail qualité du service", close: "Fermer",
    yes: "Oui", no: "Non", na: "N/D",
    degradationReasons: "Raisons de la dégradation",
    noAutoNote: "Cette recommandation n'entraîne aucune action automatique. Elle doit être validée manuellement.",
  },
  en: {
    pageTitle: "Service Quality",
    pageSub: "Quality intelligence — analysis and recommendations. No automatic depublication: the administrator decides.",
    refresh: "Refresh",
    total: "Analyzed services", degraded: "Degradation detected", monitoring: "Monitoring", suspended: "Suspended",
    autoOff: "Auto-suspend: DISABLED", autoOn: "Auto-suspend: ENABLED",
    filters: "Filters", allStatuses: "All statuses", allActions: "All actions", allRisks: "All risk levels",
    onlyDegraded: "Degraded only",
    search: "Search a service…",
    colService: "Service", colProvider: "Provider", colPlatform: "Platform", colStatus: "Status",
    colQuality: "Quality Score", colConfidence: "Confidence", colAction: "Recommended action", colRisk: "Risk",
    colOrders: "Orders", colComplaints: "Complaints", colDegr: "Degradation", colDetail: "Detail",
    view: "View", noItems: "No service.",
    scoreLabel: "Quality Score", confidenceLabel: "Confidence", statusLabel: "Status",
    recommendation: "Recommended action", reasons: "Recommendation reasons", nextAction: "Next action",
    metricsUsed: "Metrics used", unavailable: "Unavailable data",
    recentVsHist: "Recent vs historical evolution", recentQ: "Recent quality", histQ: "Historical quality",
    events: "Quality events", complaints: "Attributed complaints", noComplaints: "No attributed complaint.",
    supplierClaims: "Supplier claims (unverified)", detectedRisks: "Detected risks (metadata)",
    contradictions: "Contradictions", warnings: "Warnings", missing: "Missing information",
    completion: "Completion rate", partial: "Partial rate", refund: "Refund rate", complaintRate: "Complaint rate",
    orderCount: "Orders", recentOrders: "Recent orders",
    decisionHistory: "Decision history",
    actions: "Admin actions", reason: "Reason (required)",
    approve: "Approve", keep: "Keep published", monitor: "Monitor", suspend: "Suspend", reactivate: "Reactivate", reanalyze: "Re-analyze",
    reasonRequired: "A reason is required for this action", actionDone: "Action applied", reanalyzed: "Service re-analyzed",
    detailTitle: "Service quality detail", close: "Close",
    yes: "Yes", no: "No", na: "N/A",
    degradationReasons: "Degradation reasons",
    noAutoNote: "This recommendation triggers no automatic action. It must be validated manually.",
  },
};
const useT = () => { const { i18n } = useTranslation(); return TXT[normLang(i18n.language)] || TXT.fr; };

const STATUS_STYLE = {
  PUBLISHED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  APPROVED: "bg-sky-500/10 text-sky-500 border-sky-500/30",
  MONITORING: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  REVIEW_REQUIRED: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  SUSPENDED: "bg-rose-500/10 text-rose-500 border-rose-500/30",
  REJECTED: "bg-rose-500/15 text-rose-600 border-rose-500/40",
  DRAFT: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  ANALYZING: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};
const ACTION_STYLE = {
  KEEP: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  MONITOR: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  REVIEW: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  SUSPEND: "bg-rose-500/10 text-rose-500 border-rose-500/30",
  REJECT: "bg-rose-500/15 text-rose-600 border-rose-500/40",
  APPROVE: "bg-sky-500/10 text-sky-500 border-sky-500/30",
};
const RISK_STYLE = {
  GREEN: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  YELLOW: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  ORANGE: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  RED: "bg-rose-500/10 text-rose-500 border-rose-500/30",
};
const NA = "NOT_AVAILABLE";
const pct = (v) => (v === NA || v == null ? null : `${(v * 100).toFixed(1)}%`);

function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-display text-lg font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

function ScoreBar({ value, tone }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
      </div>
      <span className="tabular-nums text-xs">{value == null ? "—" : value}</span>
    </div>
  );
}

function DetailModal({ serviceId, onClose, onActed }) {
  const L = useT();
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/service-quality/${serviceId}`).then((r) => setD(r.data))
      .catch((e) => toast.error(apiError(e?.response?.data?.detail)));
  }, [serviceId]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, needReason) => {
    if (needReason && !reason.trim()) { toast.error(L.reasonRequired); return; }
    setBusy(true);
    try {
      await api.post(`/admin/service-quality/${serviceId}/action`, { action, reason });
      toast.success(L.actionDone); onActed?.(); load();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const reanalyze = async () => {
    setBusy(true);
    try { await api.post(`/admin/service-quality/${serviceId}/reanalyze`); toast.success(L.reanalyzed); onActed?.(); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  if (!d) return null;
  const p = d.profile; const ex = d.explanation;
  const risks = p.detected_risks || {};
  const claims = p.supplier_claims || {};
  const Row = ({ k, v }) => (
    <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="my-6 w-full max-w-2xl rounded-2xl border border-border bg-card p-5" data-testid="quality-detail-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold"><Gauge className="h-5 w-5 text-blue-500" /> {L.detailTitle}</h3>
          <button onClick={onClose} data-testid="quality-detail-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="mb-1 text-sm font-semibold">{p.service_name}</div>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-md border px-2 py-0.5 font-bold ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
          <span className={`rounded-md border px-2 py-0.5 font-bold ${ACTION_STYLE[p.recommended_action] || ""}`}>{p.recommended_action}</span>
          <span className={`rounded-md border px-2 py-0.5 font-semibold ${RISK_STYLE[p.risk_level] || ""}`}>{p.risk_level}</span>
          {p.degradation_detected && <span className="flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 font-semibold text-rose-500"><TrendingDown className="h-3.5 w-3.5" /> {L.colDegr}</span>}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label={L.scoreLabel} value={p.quality_score} tone="text-blue-500" testid="quality-detail-score" />
          <Stat label={L.confidenceLabel} value={`${p.confidence_score}/100`} testid="quality-detail-confidence" />
          <Stat label={L.orderCount} value={p.order_count} />
        </div>

        <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3 text-xs">
          <div className="mb-1 font-semibold text-muted-foreground">{L.reasons}</div>
          {(ex.reasons || []).map((r, i) => <div key={i}>• {r}</div>)}
          <div className="mt-2"><span className="text-muted-foreground">{L.nextAction}: </span>{ex.next_action}</div>
          <div className="mt-2 rounded bg-blue-500/10 p-2 text-blue-600" data-testid="quality-no-auto-note">{L.noAutoNote}</div>
        </div>

        {p.degradation_detected && (
          <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-xs" data-testid="quality-degradation-block">
            <div className="mb-1 font-semibold text-rose-500">{L.degradationReasons}</div>
            {(p.degradation_reasons || []).map((r, i) => <div key={i}>• {r}</div>)}
          </div>
        )}

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border p-3 text-xs">
            <div className="mb-1 font-semibold text-muted-foreground">{L.metricsUsed}</div>
            <Row k={L.completion} v={pct(p.completion_rate) ?? L.na} />
            <Row k={L.partial} v={pct(p.partial_rate) ?? L.na} />
            <Row k={L.refund} v={pct(p.refund_rate) ?? L.na} />
            <Row k={L.complaintRate} v={pct(p.complaint_rate) ?? L.na} />
            <Row k={L.orderCount} v={p.order_count} />
            <Row k={L.recentOrders} v={p.recent_order_count} />
          </div>
          <div className="rounded-lg border border-border p-3 text-xs">
            <div className="mb-1 font-semibold text-muted-foreground">{L.recentVsHist}</div>
            <Row k={L.recentQ} v={p.recent_quality_score === NA ? L.na : p.recent_quality_score} />
            <Row k={L.histQ} v={p.historical_quality_score === NA ? L.na : p.historical_quality_score} />
            <div className="mt-2 mb-1 font-semibold text-muted-foreground">{L.unavailable}</div>
            <div className="text-muted-foreground">drop_rate: {L.na} · refill_success: {L.na}</div>
          </div>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border p-3 text-xs" data-testid="quality-supplier-claims">
            <div className="mb-1 font-semibold text-muted-foreground">{L.supplierClaims}</div>
            <div>quality: {(claims.quality_claims || []).join(", ") || "—"}</div>
            <div>speed: {(claims.speed_claims || []).join(", ") || "—"}</div>
            <div>refill: {claims.refill_claim || "—"}</div>
          </div>
          <div className="rounded-lg border border-border p-3 text-xs" data-testid="quality-detected-risks">
            <div className="mb-1 font-semibold text-muted-foreground">{L.detectedRisks}</div>
            <div>{L.contradictions}: {(risks.contradictions || []).join("; ") || "—"}</div>
            <div>{L.warnings}: {(risks.warnings || []).join("; ") || "—"}</div>
            <div>{L.missing}: {(risks.missing || []).join("; ") || "—"}</div>
          </div>
        </div>

        <div className="mt-3 rounded-lg border border-border p-3 text-xs">
          <div className="mb-1 font-semibold text-muted-foreground">{L.complaints}</div>
          {(d.complaints || []).length
            ? d.complaints.map((c, i) => <div key={i}>• [{c.category}/{c.severity}] {c.subject} <span className="text-muted-foreground">{(c.created_at || "").slice(0, 10)}</span></div>)
            : <div className="text-muted-foreground">{L.noComplaints}</div>}
        </div>

        <div className="mt-3 max-h-40 overflow-auto rounded-lg border border-border p-3 text-[11px]" data-testid="quality-events">
          <div className="mb-1 font-semibold text-muted-foreground">{L.events} / {L.decisionHistory}</div>
          {(d.events || []).map((e, i) => (
            <div key={i} className="flex justify-between gap-2 border-b border-border/40 py-0.5 last:border-0">
              <span><b>{e.event_type}</b> {e.reason ? `— ${e.reason}` : ""}</span>
              <span className="whitespace-nowrap text-muted-foreground">{e.actor} · {(e.timestamp || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-3">
          <div className="mb-2 text-xs font-semibold text-muted-foreground">{L.actions}</div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="quality-action-reason"
            placeholder={L.reason} className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          <div className="flex flex-wrap gap-2">
            <button onClick={reanalyze} disabled={busy} data-testid="quality-action-reanalyze" className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-secondary"><RefreshCw className="h-3.5 w-3.5" /> {L.reanalyze}</button>
            <button onClick={() => act("approve", true)} disabled={busy} data-testid="quality-action-approve" className="flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white"><CheckCircle2 className="h-3.5 w-3.5" /> {L.approve}</button>
            <button onClick={() => act("publish", true)} disabled={busy} data-testid="quality-action-keep" className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"><ShieldCheck className="h-3.5 w-3.5" /> {L.keep}</button>
            <button onClick={() => act("monitor", true)} disabled={busy} data-testid="quality-action-monitor" className="flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white"><Eye className="h-3.5 w-3.5" /> {L.monitor}</button>
            <button onClick={() => act("suspend", true)} disabled={busy} data-testid="quality-action-suspend" className="flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"><PauseCircle className="h-3.5 w-3.5" /> {L.suspend}</button>
            <button onClick={() => act("reactivate", true)} disabled={busy} data-testid="quality-action-reactivate" className="flex items-center gap-1 rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-500"><PlayCircle className="h-3.5 w-3.5" /> {L.reactivate}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminServiceQuality() {
  const L = useT();
  const [stats, setStats] = useState({});
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("");
  const [action, setAction] = useState("");
  const [risk, setRisk] = useState("");
  const [degraded, setDegraded] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("quality_score");
  const [order, setOrder] = useState("asc");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { sort, order, limit: 50 };
      if (status) params.status = status;
      if (action) params.recommended_action = action;
      if (risk) params.risk_level = risk;
      if (degraded) params.degraded = true;
      if (q) params.search = q;
      const [l, s] = await Promise.all([
        api.get("/admin/service-quality", { params }),
        api.get("/admin/service-quality/stats"),
      ]);
      setData(l.data); setStats(s.data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, [status, action, risk, degraded, q, sort, order]);
  useEffect(() => { load(); }, [load]);

  const toggleSort = (f) => { if (sort === f) setOrder((o) => (o === "asc" ? "desc" : "asc")); else { setSort(f); setOrder("asc"); } };

  return (
    <div className="space-y-5" data-testid="admin-service-quality">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold"><Gauge className="h-5 w-5 text-blue-500" /> {L.pageTitle}</h1>
          <p className="text-sm text-muted-foreground">{L.pageSub}</p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="quality-autosuspend-flag" className={`rounded-md border px-2 py-1 text-[11px] font-bold ${stats.auto_suspend ? "border-rose-500/40 bg-rose-500/10 text-rose-500" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"}`}>
            {stats.auto_suspend ? L.autoOn : L.autoOff}
          </span>
          <button onClick={load} data-testid="quality-refresh" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-secondary"><RefreshCw className="h-4 w-4" /> {L.refresh}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={L.total} value={stats.total ?? 0} testid="quality-stat-total" />
        <Stat label={L.degraded} value={stats.degraded ?? 0} tone="text-rose-500" testid="quality-stat-degraded" />
        <Stat label={L.monitoring} value={stats.by_status?.MONITORING ?? 0} tone="text-amber-500" testid="quality-stat-monitoring" />
        <Stat label={L.suspended} value={stats.by_status?.SUSPENDED ?? 0} tone="text-rose-500" testid="quality-stat-suspended" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="quality-filter-status" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.allStatuses}</option>
          {["DRAFT", "REVIEW_REQUIRED", "APPROVED", "PUBLISHED", "MONITORING", "SUSPENDED", "REJECTED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="quality-filter-action" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.allActions}</option>
          {["KEEP", "MONITOR", "REVIEW", "SUSPEND"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="quality-filter-risk" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.allRisks}</option>
          {["GREEN", "YELLOW", "ORANGE", "RED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <input type="checkbox" checked={degraded} onChange={(e) => setDegraded(e.target.checked)} data-testid="quality-filter-degraded" /> {L.onlyDegraded}
        </label>
        <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2">
          <SearchIcon className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="quality-search" placeholder={L.search} className="bg-transparent py-1.5 text-sm outline-none" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs text-muted-foreground"><tr>
            <th className="px-2 py-2 text-left">{L.colService}</th>
            <th className="px-2 py-2 text-left">{L.colPlatform}</th>
            <th className="px-2 py-2 text-left">{L.colStatus}</th>
            <th className="cursor-pointer px-2 py-2 text-left" onClick={() => toggleSort("quality_score")} data-testid="quality-sort-score">{L.colQuality}</th>
            <th className="cursor-pointer px-2 py-2 text-left" onClick={() => toggleSort("confidence_score")} data-testid="quality-sort-confidence">{L.colConfidence}</th>
            <th className="px-2 py-2 text-left">{L.colAction}</th>
            <th className="px-2 py-2 text-left">{L.colRisk}</th>
            <th className="cursor-pointer px-2 py-2 text-right" onClick={() => toggleSort("order_count")}>{L.colOrders}</th>
            <th className="px-2 py-2 text-right">{L.colComplaints}</th>
            <th className="px-2 py-2 text-center">{L.colDegr}</th>
            <th className="px-2 py-2 text-right">{L.colDetail}</th>
          </tr></thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.service_id} className="border-t border-border" data-testid={`quality-row-${p.service_id}`}>
                <td className="max-w-[220px] truncate px-2 py-2" title={p.service_name}>{p.service_name}</td>
                <td className="px-2 py-2 text-xs">{p.platform_name || "—"}</td>
                <td className="px-2 py-2"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span></td>
                <td className="px-2 py-2"><ScoreBar value={p.quality_score} tone={p.quality_score >= 70 ? "bg-emerald-500" : p.quality_score >= 45 ? "bg-amber-500" : "bg-rose-500"} /></td>
                <td className="px-2 py-2"><ScoreBar value={p.confidence_score} tone="bg-blue-500" /></td>
                <td className="px-2 py-2"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${ACTION_STYLE[p.recommended_action] || ""}`}>{p.recommended_action}</span></td>
                <td className="px-2 py-2"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${RISK_STYLE[p.risk_level] || ""}`}>{p.risk_level}</span></td>
                <td className="px-2 py-2 text-right tabular-nums">{p.order_count}</td>
                <td className="px-2 py-2 text-right tabular-nums">{p.complaint_count || 0}</td>
                <td className="px-2 py-2 text-center">{p.degradation_detected ? <TrendingDown className="mx-auto h-4 w-4 text-rose-500" /> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => setOpenId(p.service_id)} data-testid={`quality-open-${p.service_id}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary">{L.view} <ChevronRight className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={11} className="px-2 py-6 text-center text-muted-foreground">{L.noItems}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground">{data.total} · <Activity className="inline h-3 w-3" /></div>

      {openId && <DetailModal serviceId={openId} onClose={() => setOpenId(null)} onActed={load} />}
    </div>
  );
}
